// src/pages/CouponsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import { Add, DeleteOutline, Edit, Refresh } from "@mui/icons-material";
import dayjs from "dayjs";
import {
  AdminCoupon,
  deleteAdminCoupon,
  listAdminCoupons,
  toggleAdminCoupon,
} from "../api/adminCouponsApi";
import CouponDialog from "../components/CouponDialog";
import CouponUsageDialog from "../components/CouponUsageDialog";

const CouponsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState<AdminCoupon[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [usageCouponId, setUsageCouponId] = useState<number | null>(null);

  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const active =
        activeFilter === "all" ? undefined : activeFilter === "active" ? true : false;

      const data = await listAdminCoupons({
        active,
        search: search.trim() || undefined,
      });

      setCoupons(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const filtered = useMemo(() => {
    const s = search.trim().toUpperCase();
    if (!s) return coupons;
    return coupons.filter((c) => String(c.code || "").toUpperCase().includes(s));
  }, [coupons, search]);

  const handleToggle = async (c: AdminCoupon) => {
    try {
      await toggleAdminCoupon(c.id, !c.is_active);
      await fetchCoupons();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to toggle coupon");
    }
  };

  const handleDelete = async (c: AdminCoupon) => {
    const isExpired = !!c.ends_at && dayjs(c.ends_at).isBefore(dayjs());

    if (c.is_active && !isExpired) {
      setError("Only inactive or expired coupons can be deleted.");
      return;
    }

    if ((c.total_used ?? 0) > 0) {
      setError("This coupon has usage history and cannot be deleted. Deactivate it instead.");
      return;
    }

    const confirmed = window.confirm(
      `Delete coupon ${c.code}? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await deleteAdminCoupon(c.id);
      await fetchCoupons();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to delete coupon");
    }
  };

  const formatValue = (c: AdminCoupon) => {
    const dv = Number(c.discount_value || 0);
    if (c.discount_type === "percentage") return `${dv}%`;
    return `$${dv.toFixed(2)}`;
  };

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Coupons</Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage coupon codes for online orders (POS-ready for future).
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchCoupons}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditId(null);
              setDialogOpen(true);
            }}
          >
            New Coupon
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ mt: 2, p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          <TextField
            label="Search code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchCoupons();
            }}
            fullWidth
          />

          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              label="Filter"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as any)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={fetchCoupons}>
            Apply
          </Button>
        </Stack>

        {error && (
          <Typography sx={{ mt: 2 }} color="error">
            {error}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Stack alignItems="center" py={6}>
            <CircularProgress />
          </Stack>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary">No coupons found.</Typography>
        ) : (
          <Stack spacing={1}>
            {filtered.map((c) => {
              const starts = c.starts_at ? dayjs(c.starts_at).format("YYYY-MM-DD") : "—";
              const ends = c.ends_at ? dayjs(c.ends_at).format("YYYY-MM-DD") : "—";

              return (
                <Paper
                  key={c.id}
                  variant="outlined"
                  onClick={() => {
                    setUsageCouponId(c.id);
                    setUsageDialogOpen(true);
                  }}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: 1,
                    },
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6" fontWeight={800}>
                          {c.code}
                        </Typography>

                        <Chip
                          size="small"
                          label={c.is_active ? "Active" : "Inactive"}
                          color={c.is_active ? "success" : "default"}
                        />

                        <Chip size="small" label={`${c.applies_to}`} />
                        <Chip size="small" label={`${c.channel}`} />
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary">
                          Value: <b>{formatValue(c)}</b>
                        </Typography>
                        {c.max_discount && (
                          <Typography variant="body2" color="text.secondary">
                            Cap: <b>${Number(c.max_discount).toFixed(2)}</b>
                          </Typography>
                        )}
                        {c.min_subtotal && (
                          <Typography variant="body2" color="text.secondary">
                            Min subtotal: <b>${Number(c.min_subtotal).toFixed(2)}</b>
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Used: <b>{c.total_used ?? 0}</b>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Window: <b>{starts}</b> → <b>{ends}</b>
                        </Typography>
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Active
                        </Typography>
                        <Switch
                          checked={!!c.is_active}
                          onChange={() => handleToggle(c)}
                        />
                      </Stack>

                      <Tooltip title="Edit">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditId(c.id);
                            setDialogOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete coupon">
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(c);
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>

      <CouponDialog
        open={dialogOpen}
        couponId={editId}
        onClose={() => setDialogOpen(false)}
        onSaved={async () => {
          setDialogOpen(false);
          await fetchCoupons();
        }}
      />

      <CouponUsageDialog
        open={usageDialogOpen}
        couponId={usageCouponId}
        onClose={() => {
          setUsageDialogOpen(false);
          setUsageCouponId(null);
        }}
      />
    </Box>
  );
};

export default CouponsPage;